import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { ExchangeIngredient, Ingredient, LineStatus } from '@/api/types'
import { Input, NumberInput, UnitInput } from '@/components/ui/Field'
import { cn } from '@/lib/cn'
import { formatGrams } from '@/lib/format'
import { isTracked } from '@/lib/macros'
import { normalizeUnit } from '@/lib/units'
import { GripVertical, Plus } from 'lucide-react'
import { useState } from 'react'
import { CreateIngredientDialog, IngredientPicker, RemoveLineButton } from './IngredientPicker'

/**
 * Column layout shared by the rows and the header above them. One row per
 * line from `md` up; on a phone each line stacks into three short rows.
 */
export const LINE_COLS =
  'grid grid-cols-[1.75rem_repeat(3,minmax(0,1fr))_2rem] gap-x-1.5 gap-y-1.5 ' +
  'md:grid-cols-[1.75rem_minmax(0,1.6fr)_4.5rem_6.5rem_6rem_minmax(0,1fr)_2rem] md:gap-y-0'

/** Inputs inside the table read as cells: no border until hovered or focused. */
const CELL =
  'h-9 border-transparent bg-surface-sunken px-2 md:bg-transparent md:placeholder:text-transparent hover:border-line-strong focus:border-accent focus:bg-surface md:focus:placeholder:text-ink-faint/70'

export interface LineDraft {
  key: string
  ingredient: Ingredient | null
  amount: number | null
  /** The ingredient has macro data, so this line cannot be left weightless. */
  tracked: boolean
  displayAmount: number | null
  displayUnit: string
  note: string
  /** Where an imported line came from, kept until an ingredient is chosen. */
  imported?: ImportedLine
}

export interface ImportedLine {
  status: LineStatus | 'duplicate'
  /** The ingredient name the file used, if any. */
  name: string | null
  /** The original recipe line. */
  text: string | null
  proposal: ExchangeIngredient | null
  suggestions: Ingredient[]
}

/**
 * Grams the ingredient's own conversion helper suggests for this line, or null.
 *
 * Only offered when the entered unit is the one the helper is recorded for, and
 * only ever applied by a click: a conversion that happens on its own is a
 * conversion nobody agreed to, and it would move the macro totals silently.
 *
 * The unit is normalised before comparing, so "cloves" finds an ingredient
 * recorded as "clove" — the plural is display, never storage.
 */
function suggestedGrams(line: LineDraft): number | null {
  const { ingredient, displayAmount, displayUnit } = line
  if (!ingredient?.conversion_unit || !ingredient.grams_per_unit) return null
  if (normalizeUnit(displayUnit) !== normalizeUnit(ingredient.conversion_unit)) return null
  if (displayAmount === null || displayAmount <= 0) return null
  return Math.round(displayAmount * ingredient.grams_per_unit)
}

/**
 * One ingredient line. Its own component because the drag handle needs
 * `useSortable`, and a hook cannot live in a loop.
 */
export function IngredientLine({
  line,
  index,
  chosenIds,
  onChange,
  onRemove,
}: {
  line: LineDraft
  index: number
  chosenIds: Set<number>
  onChange: (key: string, patch: Partial<LineDraft>) => void
  onRemove: (key: string) => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: line.key })

  const suggestion = suggestedGrams(line)
  const disabled = !line.ingredient
  const imported = line.ingredient ? undefined : line.imported

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn(
        LINE_COLS,
        'relative items-start bg-surface px-2 py-2 md:py-1',
        isDragging && 'z-10 shadow-raised',
      )}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        aria-label={`Reorder ${line.ingredient?.name ?? 'ingredient line'}`}
        title="Drag to reorder"
        className="col-start-1 row-start-1 mt-1 grid size-7 shrink-0 cursor-grab touch-none select-none place-items-center rounded text-ink-faint transition-colors hover:bg-surface-sunken hover:text-ink active:cursor-grabbing"
      >
        <GripVertical size={15} aria-hidden />
      </button>

      <div className="col-span-3 col-start-2 row-start-1 min-w-0 md:col-span-1">
        {line.ingredient ? (
          <button
            type="button"
            onClick={() => onChange(line.key, { ingredient: null, tracked: false })}
            title="Change ingredient"
            className="group flex h-9 w-full items-center justify-between gap-2 rounded-[var(--radius-field)] px-2 text-left hover:bg-surface-sunken"
          >
            <span className="truncate text-sm font-medium text-ink">{line.ingredient.name}</span>
            <span className="shrink-0 text-xs text-ink-faint opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100">
              Change
            </span>
          </button>
        ) : (
          <IngredientPicker
            excludedIds={chosenIds}
            onSelect={(ingredient) =>
              onChange(line.key, { ingredient, tracked: isTracked(ingredient) })
            }
            autoFocus={index === 0 && !line.imported}
            initialTerm={imported?.status === 'duplicate' ? '' : (imported?.name ?? '')}
          />
        )}
      </div>

      <div className="col-start-2 row-start-2 md:col-start-3 md:row-start-1">
        <NumberInput
          value={line.displayAmount}
          onValueChange={(displayAmount) => onChange(line.key, { displayAmount })}
          placeholder="Qty"
          min={0}
          disabled={disabled}
          aria-label="Amount as written"
          className={CELL}
        />
      </div>
      <div className="col-start-3 row-start-2 md:col-start-4 md:row-start-1">
        <UnitInput
          value={line.displayUnit}
          onChange={(event) => onChange(line.key, { displayUnit: event.target.value })}
          placeholder="Unit"
          disabled={disabled}
          aria-label="Unit as written"
          className={CELL}
        />
      </div>
      <div className="col-start-4 row-start-2 md:col-start-5 md:row-start-1">
        <NumberInput
          value={line.amount}
          onValueChange={(amount) => onChange(line.key, { amount })}
          placeholder="Grams"
          min={0}
          disabled={disabled}
          aria-label="Grams"
          suffix={line.amount === null ? undefined : 'g'}
          className={cn(
            CELL,
            line.tracked && line.amount === null && 'border-caution/60 hover:border-caution',
          )}
        />
        {suggestion !== null && suggestion !== line.amount ? (
          <button
            type="button"
            onClick={() => onChange(line.key, { amount: suggestion })}
            className="mt-1 rounded-full bg-accent-soft px-2 py-0.5 text-micro font-medium text-accent-ink hover:bg-accent/15"
            title={`${line.displayAmount} ${line.displayUnit} ≈ ${formatGrams(suggestion)}`}
          >
            Use {formatGrams(suggestion)}
          </button>
        ) : null}
      </div>
      <div className="col-span-3 col-start-2 row-start-3 md:col-span-1 md:col-start-6 md:row-start-1">
        <Input
          value={line.note}
          onChange={(event) => onChange(line.key, { note: event.target.value })}
          placeholder="Note"
          disabled={disabled}
          aria-label="Note"
          className={CELL}
        />
      </div>

      <div className="col-start-5 row-start-1 mt-0.5 md:col-start-7">
        <RemoveLineButton onClick={() => onRemove(line.key)} />
      </div>

      {imported ? (
        <ImportHint
          imported={imported}
          chosenIds={chosenIds}
          onSelect={(ingredient) =>
            onChange(line.key, { ingredient, tracked: isTracked(ingredient) })
          }
        />
      ) : null}
    </div>
  )
}

const HINTS: Record<ImportedLine['status'], (name: string | null) => string> = {
  matched: () => 'Choose the ingredient.',
  new: (name) => `“${name}” is not in the library yet. Create it with the macros from the file, or pick an existing ingredient.`,
  unknown: (name) => `“${name}” is not in the library, and the file did not define it.`,
  text_only: () => 'The file gave no ingredient name. Pick one.',
  duplicate: (name) => `“${name}” is already a line above. Merge the amounts there and remove this line, or pick another ingredient.`,
}

/** What the import said about a line that still needs an ingredient. */
function ImportHint({
  imported,
  chosenIds,
  onSelect,
}: {
  imported: ImportedLine
  chosenIds: Set<number>
  onSelect: (ingredient: Ingredient) => void
}) {
  const [creating, setCreating] = useState(false)
  const suggestions = imported.suggestions.filter((item) => !chosenIds.has(item.id))
  const canCreate = imported.status === 'new' || imported.status === 'unknown'

  return (
    <div className="col-span-4 col-start-2 row-start-4 mt-1 space-y-1.5 rounded-[var(--radius-field)] bg-caution-soft px-2.5 py-2 text-micro text-caution md:col-span-6 md:row-start-2">
      <p className="text-pretty">{HINTS[imported.status](imported.name)}</p>
      {imported.text ? (
        <p className="text-ink-soft">
          Original line: <span className="font-medium">“{imported.text}”</span>
        </p>
      ) : null}
      {canCreate || suggestions.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {canCreate && imported.name ? (
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="inline-flex items-center gap-1 rounded-full bg-accent px-2.5 py-0.5 font-medium text-on-accent hover:bg-accent-hover"
            >
              <Plus size={12} aria-hidden />
              Create “{imported.name}”
            </button>
          ) : null}
          {suggestions.length > 0 ? <span className="text-ink-faint">Did you mean</span> : null}
          {suggestions.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => onSelect(item)}
              className="rounded-full border border-line-strong bg-surface px-2.5 py-0.5 font-medium text-ink hover:border-accent"
            >
              {item.name}
            </button>
          ))}
        </div>
      ) : null}
      {canCreate ? (
        <CreateIngredientDialog
          open={creating}
          initialName={imported.name ?? ''}
          initialValues={imported.proposal}
          onClose={() => setCreating(false)}
          onCreated={(ingredient) => {
            setCreating(false)
            onSelect(ingredient)
          }}
        />
      ) : null}
    </div>
  )
}
